import React, { Component } from 'react';
import { Route } from 'react-router-dom';
import ConsoleLayout from 'components/features/ConsoleLayout';
import DevicesPage from 'pages/DevicesPage/DevicesPage';
import SingleDevicePage from 'pages/SingleDevicePage/SingleDevicePage';
import ActionsPage from 'pages/ActionsPage/ActionsPage';
// import MonitorPage from 'pages/MonitorPage/MonitorPage';
import UserPage from 'pages/UserPage/UserPage';
import AdminPage from 'pages/AdminPage/AdminPage';
import ChangePasswordPage from 'pages/ChangePasswordPage/ChangePasswordPage';
import 'pages/ConsolePage/ConsolePage.css';
import {
  updateSoftwareVersions,
  setUserInfo,
  doFetchAlarms,
  storeAllDeviceAlarms,
  doGetSupportedCommissioningWebsiteVersions,
  setConsolePageSize,
  doUpdateRolesFromStorage,
  doUpdatePermissionsFromStorage
} from 'redux/actions';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import classNames from 'classnames';
import Modal from 'components/shared/Modal/Modal';
import { withAuth } from 'components/shared/Auth';
import { getRelativeFilepath } from 'common/utils/helpers';
import { isItBundle } from 'common/utils/deviceTwinUtils';
import { Status } from 'common/utils/constants';
import ReactTooltip from 'react-tooltip';

import UsersManagementPage from 'pages/UsersManagementPage/UsersManagementPage';
import RoleConfigPage from 'pages/RoleConfigPage/RoleConfigPage';
import ToastNotificationProvider from 'components/shared/ToastNotifications/ToastNotificationProvider';
import StatusNotifications from 'components/shared/StatusNotifications/StatusNotifications';
import PivotOverviewPage from './../PivotOverviewPage/PivotOverviewPage';
import EchoOverviewPage from '../EchoOverviewPage/EchoOverviewPage';
import ChartExplorerPage from 'pages/ChartExplorerPage/ChartExplorerPage';

const INTERNAL_SERVER_ERROR = '500';

/**
 * Customized Component: Device view console page
 *
 * @class ConsolePage
 * @file ConsolePage.js
 * @author Christophe Vidal
 *
 */
class ConsolePage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isAlarmsPolling: false,
      alarmFetchingErrorCode: null,
      fetchDevicesFailedModalOpen: false,
      fetchDevicesFailedAcknowledged: false //after the user closes the error popup, don't show it again
    };

    this.hasHeartbeat = false;
  }

  onResize = () => {
    this.setState({ height: window.innerHeight });
    this.props.dispatch(setConsolePageSize({ width: window.innerWidth, height: window.innerHeight }));
  };

  componentDidMount() {
    /*updating state creates graceful re-renders. React must manage the stack of component state
    updates such that intermediate state updates get skipped (so, sliding a window to resize it
    by 1000 pixels doesn't generate 1000 re-renders). Using forceUpdate() was less graceful.*/

    window.addEventListener('resize', this.onResize);
    this.onResize();

    if (!this.props.auth.isAuthenticatedLS()) {
      this.props.history.push('/login');
      return;
    }

    this.updateUserInfo();
    this.props.dispatch(doUpdatePermissionsFromStorage());
    this.props.dispatch(doUpdateRolesFromStorage());
    this.props.dispatch(updateSoftwareVersions(''));
    this.props.dispatch(doGetSupportedCommissioningWebsiteVersions());

    this.shouldStopTimer = false;
  }

  componentDidUpdate() {
    this.savePath();
    if (this.props.fetchDevicesStatus === Status.FAILURE) {
      if (!this.state.fetchDevicesFailedModalOpen && !this.state.fetchDevicesFailedAcknowledged) {
        this.setState({ fetchDevicesFailedModalOpen: true });
      }
    }

    if (this.props.fetchDevicesStatus === Status.SUCCESS) {
      if (this.props.fetchAlarmsStatus == null) {
        this.fetchAlarms();
      }
    }

    if (this.hasHeartbeat === false) {
      this.heartbeatTimer = setInterval(() => {
        if (this.props.auth.isTokenApproachingExpiry()) {
          //attempt to renew session
          this.props.auth
            .renewSession()
            .then(() => {})
            .catch(() => {
              this.props.history.push('/login');
            });
        }
      }, 15000);

      this.hasHeartbeat = true;
    }

    if (this.isDelayingAlarmCalls && this.props.fetchAlarmsStatus === Status.SUCCESS) {
      clearTimeout(this.delayedAlarmCallTimer);
      this.isDelayingAlarmCalls = false;
    }
    if (
      !this.isDelayingAlarmCalls &&
      this.props.fetchAlarmsStatus === Status.FAILURE &&
      this.state.alarmFetchingErrorCode === INTERNAL_SERVER_ERROR
    ) {
      let time = 15000; // initial time to delay alarm calls
      this.isDelayingAlarmCalls = true;

      this.delayedAlarmCallTimer = setTimeout(this.runAlarmTimer, time, time);
    }

    ReactTooltip.rebuild();
  }

  componentWillUnmount() {
    if (this.heartbeatTimer != null) {
      clearInterval(this.heartbeatTimer);
    }

    if (this.isDelayingAlarmCalls) {
      clearTimeout(this.isDelayingAlarmCalls);
    }

    this.shouldStopTimer = true;
    window.removeEventListener('resize', this.onResize);
  }

  /** Recursively call fetch alarms */
  runAlarmTimer = (time) => {
    this.fetchAlarms();
    time *= 2;

    // start next timeout when still mounted and status is failure
    if (
      !this.shouldStopTimer &&
      this.props.fetchAlarmsStatus === Status.FAILURE &&
      this.state.alarmFetchingErrorCode === INTERNAL_SERVER_ERROR
    ) {
      setTimeout(this.runAlarmTimer, time, time);
    }
  };

  fetchAlarms = () => {
    this.props.dispatch(
      doFetchAlarms(
        null, // deviceIDs,
        null,
        null,
        ['active'],
        null,
        null,
        1000,
        null,
        ['severity'],
        (result) => {
          this.setState({ alarmFetchingErrorCode: null });
          this.props.dispatch(storeAllDeviceAlarms(result));
        },
        (error) => {
          console.log('doFetchAlarmsError', error);
          this.setState({ alarmFetchingErrorCode: error });
        },
        []
      )
    );
  };

  updateUserInfo() {
    this.props.auth.getUserInfo((err, userInfo) => {
      if (err) {
        console.log('user info error:', err);
      } else {
        this.props.dispatch(setUserInfo(userInfo));
      }
    });
  }

  savePath = () => {
    //save page if on a single device page, otherwise we'll just redirect to the devices page
    if (this.props.location.pathname.includes('devices')) {
      localStorage.setItem('redirectUrl', this.props.location.pathname + this.props.location.search);
    } else {
      localStorage.setItem('redirectUrl', '/console/devices');
    }
  };

  render() {
    document.documentElement.style.overflowX = 'auto';
    document.documentElement.style.overflowY = 'hidden';
    let devicesPath = this.props.match.path + '/devices';
    // let monitorPath = this.props.match.path + '/monitor';
    let adminPath = this.props.match.path + '/admin';
    // let sitePath = this.props.match.path + '/site';
    let userPath = this.props.match.path + '/user';
    let actionsPath = this.props.match.path + '/actions';
    let changePasswordPath = this.props.match.path + '/change-password';
    let chartExplorerPath = this.props.match.path + '/chart-explorer';

    //if no permissions return null
    try {
      if (!this.props.auth.isAuthenticatedLS() || this.props.permissions == null) {
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }

    let errorModal = null;
    if (this.state.fetchDevicesFailedModalOpen) {
      errorModal = (
        <Modal
          closeTargetRef={null}
          close={() => {
            this.setState({ fetchDevicesFailedAcknowledged: true, fetchDevicesFailedModalOpen: false });
          }}
          animate={false}
          animateX={false}
          opaque={false}
          height={220}
          width={340}
          zIndex={2}
        >
          <div className="ConsolePage_GetDevices_ErrorModal">
            <div className="ConsolePage_GetDevices_ErrorModal_Title">Error</div>
            <div className="ConsolePage_GetDevices_ErrorModal_Message">
              Failed to retrieve info for devices. Please try refreshing the page, and if the error persists, contact
              support at
              <a href="mailto:support@pivotalcommware.com"> support@pivotalcommware.com</a>
            </div>
            <button
              className="Global_RoundedButton ConsolePage_GetDevices_ErrorModal_Button"
              onClick={() => {
                this.setState({ fetchDevicesFailedAcknowledged: true, fetchDevicesFailedModalOpen: false });
              }}
            >
              OK
            </button>
          </div>
        </Modal>
      );
    }

    return (
      <div>
        {errorModal}
        <ToastNotificationProvider />
        <StatusNotifications />
        <ConsoleLayout
          className={classNames('scroll-component', this.props.consolePageClass)}
          mapSize={this.props.style.mapSize}
          juanStyle={this.props.style.juanStyle}
        >
          <div id="GlobalModalContainer"></div>
          <Route exact path={devicesPath} render={(props) => <DevicesPage {...props} />} />
          <Route
            path={devicesPath + '/:id'}
            render={() => {
              let relativePath = getRelativeFilepath(this.props.location.pathname);

              if (relativePath !== 'detail') {
                let isBundle = isItBundle(relativePath);
                if (isBundle) return <Redirect to={devicesPath + 'pivot/detail?' + relativePath} />;
              }
            }}
          />
          <Route path={devicesPath + '/detail'} render={(props) => <SingleDevicePage {...props} />} />
          <Route path={devicesPath + '/echo'} render={(props) => <EchoOverviewPage {...props} />} />
          <Route path={devicesPath + '/pivot'} render={(props) => <PivotOverviewPage {...props} />} />
          <Route exact path={adminPath} render={(props) => <AdminPage {...props} />} />
          <Route exact path={actionsPath} render={(props) => <ActionsPage {...props} />} />
          {/* <Route exact path={monitorPath} render={(props) => <MonitorPage {...props} />} /> */}
          <Route exact path={userPath} render={(props) => <UserPage {...props} />} />
          <Route exact path={changePasswordPath} render={(props) => <ChangePasswordPage {...props} />} />
          <Route exact path={adminPath + '/users-management'} render={(props) => <UsersManagementPage {...props} />} />
          <Route exact path={adminPath + '/roles-config'} render={(props) => <RoleConfigPage {...props} />} />
          <Route exact path={chartExplorerPath + '/detail'} render={(props) => <ChartExplorerPage {...props} />} />
        </ConsoleLayout>
        <ReactTooltip className="ConsolePage_Tooltip" place={'bottom'} delayShow={150} type={'dark'} effect={'solid'} />
      </div>
    );
  }
}

export default withAuth(
  withRouter(
    connect((store) => {
      const activeTab = store.ui.devicesPage.activeTab;
      const searchDevicesStatusName = `searchComponentFilteredDevicesV2_deviceTable_${activeTab}`;

      return {
        style: store.style,
        fetchDevicesStatus: store.statuses?.[searchDevicesStatusName],
        fetchAlarmsStatus: store.statuses.fetchAlarms,
        permissions: store.data.rolePermissions != null ? store.data.rolePermissions.permissions : null
      };
    })(ConsolePage)
  )
);
